import { useEffect } from 'react';
import { Modal, Form, Select, Divider, Typography } from 'antd';
import { LinkOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { OrganizationRelationType } from '@evimo/core/modules/organization-relationship/domain/organization-relation.type';
import type { CreateOrganizationRelationshipResponseDto } from '@evimo/client/models/CreateOrganizationRelationshipResponseDto';
import type { FindOrganizationDto } from '@evimo/types/api/organization/find-organization/find-organization.dto';

const { Text } = Typography;

export type FormValues = {
  relationType: OrganizationRelationType;
  orgId: string;
  partnerOrgId: string;
};

type Props = {
  open: boolean;
  editing: CreateOrganizationRelationshipResponseDto | null;
  selectedParentId: string | null;
  orgOptions: { label: string; value: string }[];
  organizations: FindOrganizationDto[];
  relationships: CreateOrganizationRelationshipResponseDto[];
  isSubmitting: boolean;
  onCancel: () => void;
  onOk: (
    values: FormValues,
    editing: CreateOrganizationRelationshipResponseDto | null,
  ) => void | Promise<void>;
};

export function RelationshipModal({
  open,
  editing,
  selectedParentId,
  orgOptions,
  organizations,
  relationships,
  isSubmitting,
  onCancel,
  onOk,
}: Props) {
  const { t } = useTranslation('organizationRelationship');
  const [form] = Form.useForm<FormValues>();

  // Watch org id from form value
  const orgIdValue = Form.useWatch('orgId', form) || null;

  // Get children of selected org (organizations where parentId === orgIdValue)
  // Filter out children that already have a relationship with this org (unless editing that relationship)
  const childOptions = orgIdValue
    ? organizations
        .filter((org) => {
          // Must be a child of the selected org
          if (org.parentId !== orgIdValue || org.id === orgIdValue) {
            return false;
          }

          // If editing, allow the current partner to be shown
          if (editing && org.id === editing.partnerOrgId) {
            return true;
          }

          // Exclude if already has a relationship with this org
          const hasExistingRelationship = relationships.some(
            (rel) => rel.orgId === orgIdValue && rel.partnerOrgId === org.id,
          );

          return !hasExistingRelationship;
        })
        .map((org) => ({ label: org.name, value: org.id }))
    : [];

  useEffect(() => {
    if (!open) return;

    if (editing) {
      form.setFieldsValue({
        relationType: editing.relationType,
        orgId: editing.orgId,
        partnerOrgId: editing.partnerOrgId,
      });
      return;
    }

    // Pre-fill orgId if selectedParentId is set
    if (selectedParentId) {
      form.setFieldsValue({
        orgId: selectedParentId,
        partnerOrgId: undefined,
      });
    } else {
      form.resetFields();
    }
  }, [open, editing, selectedParentId, form]);

  const handleParentChange = () => {
    form.setFieldValue('partnerOrgId', undefined);
  };

  const handleOk = async () => {
    const values = await form.validateFields();
    onOk(values, editing);
    form.resetFields();
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      onOk={handleOk}
      confirmLoading={isSubmitting}
      title={
        <div className="app-modal-title">
          <LinkOutlined />
          <Text>
            {editing
              ? t('modal.editTitle', 'Edit Relationship')
              : t('modal.createTitle', 'Add Relationship')}
          </Text>
        </div>
      }
      okText={
        editing
          ? t('modal.saveChanges', 'Save Changes')
          : t('modal.create', 'Create')
      }
      cancelText={t('common.cancel', 'Cancel')}
      destroyOnHidden
      width={480}
    >
      <Divider style={{ marginTop: 8 }} />
      <Form form={form} layout="vertical" className="app-form">
        <Form.Item
          name="relationType"
          label={t('relationship.relationType', 'Relation Type')}
          rules={[
            {
              required: true,
              message: t(
                'validation.relationTypeRequired',
                'Please select a relation type',
              ),
            },
          ]}
        >
          <Select
            options={Object.values(OrganizationRelationType).map((type) => ({
              value: type,
              label: t(`relationType.${type}`, type.replace(/_/g, ' ')),
            }))}
          />
        </Form.Item>

        <Form.Item
          name="orgId"
          label={t('relationship.organization', 'Organization')}
          rules={[
            {
              required: true,
              message: t(
                'validation.parentOrgRequired',
                'Please select an organization',
              ),
            },
          ]}
        >
          <Select
            showSearch
            allowClear
            options={orgOptions}
            optionFilterProp="label"
            onChange={handleParentChange}
          />
        </Form.Item>

        {orgIdValue && (
          <Form.Item
            name="partnerOrgId"
            label={t('relationship.partnerOrg', 'Partner Organization')}
            rules={[
              {
                required: true,
                message: t(
                  'validation.clientRequired',
                  'Please select a partner',
                ),
              },
            ]}
          >
            <Select
              showSearch
              allowClear
              options={childOptions}
              optionFilterProp="label"
            />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}
